import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle, Lock, Shield, Copy, Key, RefreshCw, AlertTriangle, UserPlus, Trash2, Users, FileSignature, CreditCard } from 'lucide-react';
import AccessCodesSection from './AccessCodesSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_QUESTIONS = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What is your mother's maiden name?"
];

const AdminSettingsPage = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Password
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  // Security Questions (3 pairs)
  const [sqForm, setSqForm] = useState([
    { question: ALL_QUESTIONS[0], answer: '' },
    { question: ALL_QUESTIONS[1], answer: '' },
    { question: ALL_QUESTIONS[2], answer: '' }
  ]);
  const [sqSaved, setSqSaved] = useState(false);
  const [savingSQ, setSavingSQ] = useState(false);

  // Recovery Code
  const [recoveryCode, setRecoveryCode] = useState('');
  const [generatingCode, setGeneratingCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);

  // Multi-admin management
  const [admins, setAdmins] = useState([]);
  const [newAdminForm, setNewAdminForm] = useState({ name: '', email: '', password: '' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [deletingAdminId, setDeletingAdminId] = useState('');

  // Certificate signature settings
  const [certSettings, setCertSettings] = useState({ signature_name: '', director_title: '' });
  const [savingCert, setSavingCert] = useState(false);

  // Payment settings (fee + currency) — drives Stripe Checkout amount & currency.
  const [paySettings, setPaySettings] = useState({ participation_fee: 10, currency: 'cad', event_name: 'Brain Gym Olympiad' });
  const [savingPay, setSavingPay] = useState(false);

  useEffect(() => {
    loadRecoveryStatus();
    loadAdmins();
    loadCertSettings();
    loadPaySettings();
  }, []);

  const loadPaySettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/settings`, { withCredentials: true });
      setPaySettings({
        participation_fee: Number(res.data?.participation_fee ?? 10),
        currency: (res.data?.currency || 'cad').toLowerCase(),
        event_name: res.data?.event_name || 'Brain Gym Olympiad',
      });
    } catch {}
  };

  const savePaySettings = async () => {
    const fee = Number(paySettings.participation_fee);
    if (!Number.isFinite(fee) || fee <= 0) {
      setError('Participation fee must be a positive number');
      return;
    }
    setSavingPay(true); setError('');
    try {
      await axios.put(`${API_URL}/api/admin/settings`, {
        participation_fee: fee,
        currency: paySettings.currency,
        event_name: paySettings.event_name,
        event_active: false,
        event_started: false,
      }, { withCredentials: true });
      setSuccess(`Payment settings saved (${fee.toFixed(2)} ${paySettings.currency.toUpperCase()}).`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save payment settings');
    } finally { setSavingPay(false); }
  };

  const loadCertSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/cert-settings`, { withCredentials: true });
      setCertSettings({
        signature_name: res.data?.signature_name || '',
        director_title: res.data?.director_title || '',
      });
    } catch {}
  };

  const saveCertSettings = async () => {
    const { signature_name, director_title } = certSettings;
    if (!signature_name.trim() || !director_title.trim()) {
      setError('Signature name and director title are required'); return;
    }
    setSavingCert(true); setError('');
    try {
      await axios.patch(`${API_URL}/api/admin/cert-settings`, { signature_name, director_title }, { withCredentials: true });
      setSuccess('Certificate signature updated. New downloads will use this.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingCert(false); }
  };

  const loadAdmins = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/admins`, { withCredentials: true });
      setAdmins(res.data.admins || []);
    } catch {}
  };

  const createNewAdmin = async () => {
    const { name, email, password } = newAdminForm;
    if (!name.trim() || !email.trim() || password.length < 8) {
      setError('Name, valid email, and password (min 8 chars) are required');
      return;
    }
    setCreatingAdmin(true); setError('');
    try {
      await axios.post(`${API_URL}/api/admin/create-admin`, { name, email, password }, { withCredentials: true });
      setNewAdminForm({ name: '', email: '', password: '' });
      setSuccess(`Admin "${name}" created successfully`);
      setTimeout(() => setSuccess(''), 3000);
      await loadAdmins();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create admin');
    } finally { setCreatingAdmin(false); }
  };

  const deleteAdmin = async (id, email) => {
    if (!window.confirm(`Delete admin "${email}"? This cannot be undone.`)) return;
    setDeletingAdminId(id); setError('');
    try {
      await axios.delete(`${API_URL}/api/admin/admins/${id}`, { withCredentials: true });
      setSuccess(`Admin "${email}" removed`);
      setTimeout(() => setSuccess(''), 3000);
      await loadAdmins();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete admin');
    } finally { setDeletingAdminId(''); }
  };

  const loadRecoveryStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/recovery-status`, { withCredentials: true });
      setRecoveryCode(res.data.recovery_code || '');
      const saved = res.data.security_questions || [];
      if (saved.length === 3) {
        setSqForm(saved.map((s, i) => ({ question: s.question, answer: '' })));
        setSqSaved(true);
      }
    } catch (e) {}
  };

  const changePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { setError('New passwords do not match'); return; }
    if (passwordForm.newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setChangingPassword(true); setError('');
    try {
      await axios.post(`${API_URL}/api/admin/change-password`, {
        current_password: passwordForm.currentPassword, new_password: passwordForm.newPassword
      }, { withCredentials: true });
      setSuccess('Password changed!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to change password'); }
    finally { setChangingPassword(false); }
  };

  const saveSecurityQuestions = async () => {
    for (let i = 0; i < 3; i++) {
      if (!sqForm[i].answer.trim()) { setError(`Please provide an answer for question ${i + 1}`); return; }
    }
    setSavingSQ(true); setError('');
    try {
      await axios.post(`${API_URL}/api/admin/setup-security-questions`, {
        questions: sqForm.map(s => ({ question: s.question, answer: s.answer }))
      }, { withCredentials: true });
      setSqSaved(true);
      setSqForm(f => f.map(s => ({ ...s, answer: '' })));
      setSuccess('Security questions saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    finally { setSavingSQ(false); }
  };

  const generateCode = async () => {
    setGeneratingCode(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/api/admin/generate-recovery-code`, {}, { withCredentials: true });
      setRecoveryCode(res.data.recovery_code);
      setSuccess('Recovery code generated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to generate code'); }
    finally { setGeneratingCode(false); }
  };

  const copyCode = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(recoveryCode);
      } else {
        const ta = document.createElement('textarea');
        ta.value = recoveryCode;
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      // Silently ignore — user can select manually
    }
  };

  const fullSystemReset = async () => {
    setResetting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/full-system-reset`, {}, { withCredentials: true });
      setSuccess('Full system reset complete. All data has been wiped.');
      setResetConfirmText('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset system');
    } finally {
      setResetting(false);
    }
  };

  const updateSQ = (index, field, value) => {
    setSqForm(f => f.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Settings</h1>
        <p className="text-sm text-[#64748b] mt-1">Password and account recovery.</p>
      </div>

      {error && (
        <div className="bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] px-4 py-3 rounded-2xl mb-4 text-sm font-medium">
          {error} <button onClick={() => setError('')} className="ml-2 font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="bg-[#dcfce7] border-2 border-[#86efac] text-[#16a34a] px-4 py-3 rounded-2xl mb-4 text-sm font-medium flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> {success}
        </div>
      )}

      <div className="space-y-6">
        {/* Change Password */}
        <div className="card-olympiad p-6">
          <h2 className="text-base font-bold text-[#0f172a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Lock className="w-5 h-5 text-[#2565eb]" /> Change Password
          </h2>
          <div className="space-y-3 max-w-sm">
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Current Password</label>
              <input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))} className="input-olympiad" data-testid="current-password" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">New Password</label>
              <input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))} className="input-olympiad" data-testid="new-password" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Confirm New Password</label>
              <input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))} className="input-olympiad" data-testid="confirm-password" />
            </div>
            <button onClick={changePassword} disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword} className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50" data-testid="change-password-button">
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Change Password
            </button>
          </div>
        </div>

        {/* Certificate Signature Settings */}
        <div className="card-olympiad p-6">
          <h2 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <FileSignature className="w-5 h-5 text-[#2565eb]" /> Certificate Signature
          </h2>
          <p className="text-sm text-[#64748b] mb-4">Customize the italic signature name and director title that appear at the bottom of every certificate (Winner + Participation). Changes apply to new downloads instantly — no redeploy needed.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Signature Name (italic script)</label>
              <input
                type="text"
                value={certSettings.signature_name}
                onChange={(e) => setCertSettings(s => ({ ...s, signature_name: e.target.value }))}
                className="input-olympiad"
                placeholder="e.g. S Das"
                maxLength={80}
                data-testid="cert-signature-name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Director Title (below the line)</label>
              <input
                type="text"
                value={certSettings.director_title}
                onChange={(e) => setCertSettings(s => ({ ...s, director_title: e.target.value }))}
                className="input-olympiad"
                placeholder="e.g. Brain Gym Jr. Director"
                maxLength={120}
                data-testid="cert-director-title"
              />
            </div>
          </div>
          <button
            onClick={saveCertSettings}
            disabled={savingCert || !certSettings.signature_name.trim() || !certSettings.director_title.trim()}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 mt-4"
            data-testid="save-cert-signature-btn"
          >
            {savingCert ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Save Signature
          </button>
        </div>

        {/* Payment Settings — drives Stripe Checkout fee & currency */}
        <div className="card-olympiad p-6" data-testid="payment-settings-card">
          <h2 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <CreditCard className="w-5 h-5 text-[#2565eb]" /> Payment Settings
          </h2>
          <p className="text-sm text-[#64748b] mb-4">
            Sets the fee and currency charged on Stripe Checkout. Changes apply to <strong>new registrations</strong> immediately — previously created Stripe sessions keep their original amount/currency.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Participation Fee</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={paySettings.participation_fee}
                onChange={(e) => setPaySettings(s => ({ ...s, participation_fee: e.target.value }))}
                className="input-olympiad"
                placeholder="10.00"
                data-testid="pay-fee-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Currency</label>
              <select
                value={paySettings.currency}
                onChange={(e) => setPaySettings(s => ({ ...s, currency: e.target.value }))}
                className="input-olympiad"
                data-testid="pay-currency-select"
              >
                <option value="cad">CAD — Canadian Dollar</option>
                <option value="usd">USD — US Dollar</option>
                <option value="eur">EUR — Euro</option>
                <option value="gbp">GBP — British Pound</option>
                <option value="aud">AUD — Australian Dollar</option>
                <option value="inr">INR — Indian Rupee</option>
              </select>
            </div>
          </div>
          <div className="mt-3 text-xs text-[#64748b]">
            Stripe shows: <strong>{Number(paySettings.participation_fee || 0).toFixed(2)} {paySettings.currency.toUpperCase()}</strong> per registration.
          </div>
          <button
            onClick={savePaySettings}
            disabled={savingPay}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 mt-4"
            data-testid="save-payment-settings-btn"
          >
            {savingPay ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Save Payment Settings
          </button>
        </div>


        <div className="card-olympiad p-6">
          <h2 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <UserPlus className="w-5 h-5 text-[#2565eb]" /> Create New Admin
          </h2>
          <p className="text-sm text-[#64748b] mb-4">Add another admin who can manage the system.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Full Name</label>
              <input
                type="text"
                value={newAdminForm.name}
                onChange={(e) => setNewAdminForm(f => ({ ...f, name: e.target.value }))}
                className="input-olympiad"
                placeholder="Jane Doe"
                data-testid="new-admin-name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Email</label>
              <input
                type="email"
                value={newAdminForm.email}
                onChange={(e) => setNewAdminForm(f => ({ ...f, email: e.target.value }))}
                className="input-olympiad"
                placeholder="jane@example.com"
                data-testid="new-admin-email"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Password (min 8 chars)</label>
              <input
                type="password"
                value={newAdminForm.password}
                onChange={(e) => setNewAdminForm(f => ({ ...f, password: e.target.value }))}
                className="input-olympiad"
                data-testid="new-admin-password"
              />
            </div>
          </div>
          <button
            onClick={createNewAdmin}
            disabled={creatingAdmin || !newAdminForm.email || !newAdminForm.name || newAdminForm.password.length < 8}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 mt-4"
            data-testid="create-admin-button"
          >
            {creatingAdmin ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create Admin
          </button>
        </div>

        {/* Manage Admins List */}
        <div className="card-olympiad p-6">
          <h2 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Users className="w-5 h-5 text-[#2565eb]" /> Manage Admins
          </h2>
          <p className="text-sm text-[#64748b] mb-4">{admins.length} admin{admins.length === 1 ? '' : 's'} have access to this system.</p>
          <div className="space-y-2" data-testid="admins-list">
            {admins.length === 0 ? (
              <p className="text-sm text-[#94a3b8]">No admins found.</p>
            ) : admins.map(a => (
              <div
                key={a.id}
                className="flex items-center justify-between bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl px-4 py-3"
                data-testid={`admin-row-${a.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#0f172a] truncate">{a.name || a.email}</p>
                    {a.is_current && (
                      <span className="text-[10px] font-bold bg-[#2565eb] text-white px-2 py-0.5 rounded-full">YOU</span>
                    )}
                  </div>
                  <p className="text-xs text-[#64748b] truncate">{a.email}</p>
                </div>
                {!a.is_current && (
                  <button
                    onClick={() => deleteAdmin(a.id, a.email)}
                    disabled={deletingAdminId === a.id}
                    className="text-[#dc2626] hover:text-[#991b1b] disabled:opacity-50 flex-shrink-0 p-2"
                    title="Remove admin"
                    aria-label="Remove admin"
                    data-testid={`delete-admin-${a.id}`}
                  >
                    {deletingAdminId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Security Questions */}
        <div className="card-olympiad p-6">
          <h2 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Shield className="w-5 h-5 text-[#2565eb]" /> Security Questions
          </h2>
          <p className="text-sm text-[#64748b] mb-5">Set 3 security questions. Any one correct answer can reset your password.</p>

          {sqSaved && (
            <div className="px-3 py-2 rounded-xl text-xs font-bold bg-[#dcfce7] text-[#16a34a] inline-block mb-4" data-testid="sq-status">
              All 3 questions set
            </div>
          )}

          <div className="space-y-4">
            {sqForm.map((s, i) => (
              <div key={i} className="bg-[#f8fafc] rounded-2xl p-4 border border-[#e2e8f0]">
                <label className="block text-xs font-bold text-[#64748b] uppercase tracking-wide mb-2">Question {i + 1}</label>
                <select
                  value={s.question}
                  onChange={(e) => updateSQ(i, 'question', e.target.value)}
                  className="input-olympiad text-sm mb-2"
                  data-testid={`sq-question-${i}`}
                >
                  {ALL_QUESTIONS.map(q => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={s.answer}
                  onChange={(e) => updateSQ(i, 'answer', e.target.value)}
                  className="input-olympiad text-sm"
                  placeholder={sqSaved ? 'Enter new answer to update' : 'Your answer'}
                  data-testid={`sq-answer-${i}`}
                />
              </div>
            ))}
          </div>

          <button
            onClick={saveSecurityQuestions}
            disabled={savingSQ || sqForm.some(s => !s.answer.trim())}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 mt-4"
            data-testid="save-security-questions"
          >
            {savingSQ ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {sqSaved ? 'Update Security Questions' : 'Save Security Questions'}
          </button>
        </div>

        {/* Recovery Code */}
        <div className="card-olympiad p-6">
          <h2 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Key className="w-5 h-5 text-[#2565eb]" /> Recovery Code
          </h2>
          <p className="text-sm text-[#64748b] mb-5">This code can be used to reset your password. It auto-regenerates after each use.</p>

          {recoveryCode ? (
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <code className="flex-1 bg-[#1e3a8a] px-5 py-4 rounded-xl text-xl font-mono font-bold text-white tracking-widest text-center select-all" data-testid="recovery-code-value">
                  {recoveryCode}
                </code>
                <button onClick={copyCode} className="px-4 py-4 rounded-xl font-bold bg-[#2565eb] text-white text-sm hover:bg-[#1e3a8a] transition-all flex items-center gap-2" data-testid="copy-recovery-code">
                  <Copy className="w-4 h-4" /> {codeCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-[#fef3c7] rounded-2xl px-4 py-3 mb-4 text-sm text-[#92400e] font-medium" data-testid="no-recovery-code">
              No recovery code generated yet. Click below to generate one.
            </div>
          )}

          <button
            onClick={generateCode}
            disabled={generatingCode}
            className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#2565eb] text-[#2565eb] hover:bg-[#eff6ff] transition-all text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="generate-recovery-code"
          >
            {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {recoveryCode ? 'Regenerate Code' : 'Generate Recovery Code'}
          </button>
        </div>

        {/* Free Registration Access Codes */}
        <AccessCodesSection />

        {/* Full System Reset */}
        <div className="card-olympiad p-6 border-2 border-[#fecaca]">
          <h2 className="text-base font-bold text-[#ef4444] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <AlertTriangle className="w-5 h-5" /> Full System Reset
          </h2>
          <p className="text-sm text-[#64748b] mb-4">
            This will permanently delete <span className="font-bold text-[#ef4444]">everything</span> — all competitions, registrations, question folders, categories, questions, certificates, and announcements. Your admin account and settings are preserved. This cannot be undone.
          </p>
          <div className="max-w-sm mb-4">
            <label className="block text-sm font-semibold text-[#475569] mb-1.5">Type <span className="font-mono text-[#ef4444]">RESET EVERYTHING</span> to confirm</label>
            <input
              type="text"
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              className="input-olympiad font-mono"
              placeholder="RESET EVERYTHING"
              data-testid="reset-confirm-input"
            />
          </div>
          <button
            onClick={fullSystemReset}
            disabled={resetting || resetConfirmText !== 'RESET EVERYTHING'}
            className="px-5 py-2.5 rounded-xl font-bold bg-[#ef4444] text-white hover:bg-[#dc2626] transition-all flex items-center gap-2 text-sm disabled:opacity-50"
            data-testid="full-system-reset-button"
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            Delete Everything & Start Fresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
